import {
  Alert,
  Box,
  DialogActions,
  DialogContent,
  Snackbar,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { CustomButton, DialogContainer, DialogTitle } from '../../../../common/components';
import { useProjectDialog } from '../hooks/useProjectDialog';

// Tabs
import GeneralDataTab from './tabs/GeneralDataTab';
import ContractTab from './tabs/ContractTab';
import TemplatesTab from './tabs/TemplatesTab';

// Diálogos
import SubscriberDialog from './dialogs/ProjectSubscriberDialog';

// Constantes para las tabs
const PROJECT_TABS = {
  GENERAL_DATA: 0,
  CONTRACT: 1,
  TEMPLATES: 2,
};

const TAB_LABELS = {
  [PROJECT_TABS.GENERAL_DATA]: 'Datos Generales',
  [PROJECT_TABS.CONTRACT]: 'Contrato',
  [PROJECT_TABS.TEMPLATES]: 'Planillas',
};

/**
 * Modal de creación/edición de proyectos con sistema de tabs
 * Siguiendo exactamente el patrón del ContractIndividualEditDialog
 */
const ProjectDialog = ({
  open,
  onClose,
  editMode,
  selectedProject,
  onSave,
  subscribers = [],
  progressTemplates = [],
  qualityTemplates = [],
}) => {
  const {
    localProject,
    activeTab,
    subscriberDialogOpen,
    selectedSubscriber,
    editModeSubscriber,
    matchingSubscribers,
    snackbar,
    modal,
    handleProjectChange,
    handleInputChange,
    handleSelectChange,
    setActiveTab,
    handleSaveProject,
    handleCloseSubscriberDialog,
    handleSaveSubscriber,
    handleInputChangeSubscriber,
    handleSubscriberSelection,
    handleInputChangeDNISubscriber,
    handleCloseSnackbar,
    handleCloseModal,
  } = useProjectDialog({
    selectedProject,
    open,
    onSave,
    editMode,
  });

  if (!open || !localProject) return null;

  const renderGeneralDataTab = () => (
    <GeneralDataTab
      localProject={localProject}
      handleInputChange={handleInputChange}
      handleSelectChange={handleSelectChange}
    />
  );

  const renderContractTab = () => (
    <ContractTab
      localProject={localProject}
      onContractChange={(contract) => {
        handleProjectChange('uuid_contract', contract?.uuid || null);
      }}
    />
  );

  const renderTemplatesTab = () => (
    <TemplatesTab
      localProject={localProject}
      handleSelectChange={handleSelectChange}
      progressTemplates={progressTemplates}
      qualityTemplates={qualityTemplates}
      editMode={editMode}
    />
  );

  return (
    <>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <DialogContainer open={modal.open} onClose={handleCloseModal} size="small">
        <DialogContent>
          <Typography>{modal.message}</Typography>
        </DialogContent>
        <DialogActions>
          <CustomButton onClick={handleCloseModal} variant="base">
            Aceptar
          </CustomButton>
        </DialogActions>
      </DialogContainer>

      <DialogContainer open={open} onClose={onClose} size="medium" minHeight="80vh">
        <DialogTitle>
          {editMode ? `EDITAR PROYECTO: ${localProject.name || ''}` : 'NUEVO PROYECTO'}
        </DialogTitle>

        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Box
            sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexShrink: 0 }}
          >
            <Tabs
              value={activeTab}
              onChange={(event, newValue) => setActiveTab(newValue)}
              scrollButtons="auto"
              variant="scrollable"
              sx={{
                '& .MuiTab-root': {
                  fontSize: '0.9rem',
                  fontWeight: 'bold',
                },
              }}
            >
              <Tab label={TAB_LABELS[PROJECT_TABS.GENERAL_DATA]} />
              <Tab label={TAB_LABELS[PROJECT_TABS.CONTRACT]} />
              <Tab label={TAB_LABELS[PROJECT_TABS.TEMPLATES]} />
            </Tabs>
          </Box>

          <Box sx={{ flex: 1, overflow: 'auto' }}>
            {activeTab === PROJECT_TABS.GENERAL_DATA && renderGeneralDataTab()}
            {activeTab === PROJECT_TABS.CONTRACT && renderContractTab()}
            {activeTab === PROJECT_TABS.TEMPLATES && renderTemplatesTab()}
          </Box>
        </Box>

        <DialogActions
          sx={{
            flexShrink: 0,
            p: 2,
            borderTop: '1px solid #e0e0e0',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1,
            '& > :not(style) ~ :not(style)': { ml: { xs: 0, sm: 1 } },
          }}
        >
          <CustomButton
            onClick={handleSaveProject}
            variant="base"
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            Guardar
          </CustomButton>
          <CustomButton
            onClick={onClose}
            variant="warning"
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            Cancelar
          </CustomButton>
        </DialogActions>
      </DialogContainer>

      <SubscriberDialog
        subscriberDialogOpen={subscriberDialogOpen}
        editModeSubscriber={editModeSubscriber}
        selectedSubscriber={selectedSubscriber}
        matchingSubscribers={matchingSubscribers}
        handleCloseSubscriberDialog={handleCloseSubscriberDialog}
        handleInputChangeSubscriber={handleInputChangeSubscriber}
        handleInputChangeDNISubscriber={handleInputChangeDNISubscriber}
        handleSubscriberSelection={handleSubscriberSelection}
        handleSaveSubscriber={handleSaveSubscriber}
      />
    </>
  );
};

export default ProjectDialog;
